import React, { useEffect, useState } from 'react';
import Information from './components/Information';
import yup from '../../libs/yup/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import Media from './components/Media';
import Facilities from './components/Facilities';
import Location from './components/Location';
import Configuration from './components/Configuration';
import { getTenantDetail, searchFacilities, updateTenant } from '../../api/sportaloka-client';
import Swal from 'sweetalert2';

const defaultValues = {
  site_name: '',
  overview_id: '',
  overview_en: '',
  facilities: [
    {
      id: '1',
      name: 'Toilet',
      icon: 'https://admindev.makaryo.web.id/img/dashboard/icons/1.svg',
      is_checked: true
    },
    {
      id: '2',
      name: 'Mushola',
      icon: 'https://admindev.makaryo.web.id/img/dashboard/icons/2.svg',
      is_checked: false
    },
    {
      id: '3',
      name: 'Ruang Ganti',
      icon: 'https://admindev.makaryo.web.id/img/dashboard/icons/3.svg',
      is_checked: true
    }
  ],
  media: [
    {
      url: '',
      is_primary: true,
      type: 'IMAGE'
    },
    {
      url: '',
      is_primary: false,
      type: 'IMAGE'
    },
    {
      url: '',
      is_primary: false,
      type: 'IMAGE'
    },
    {
      url: '',
      is_primary: false,
      type: 'IMAGE'
    },
    {
      url: '',
      is_primary: false,
      type: 'IMAGE'
    }
  ],
  lat: '',
  lng: '',
  site_address: '',
  province: null,
  city: null,
  daily_quota: 0
};

const schema = yup.object().shape({
  site_name: yup.string().label('Nama fasilitas').required('${label} wajib diisi'),
  overview_id: yup.string().label('Overview (ID)').required('${label} wajib diisi'),
  overview_en: yup.string().label('Overview (EN)').required('${label} wajib diisi'),
  media: yup.array().of(
    yup.object().shape({
      url: yup.string().when('is_primary', {
        is: true,
        then: (schema) => schema.required('Wajib memilih gambar utama'),
        otherwise: (schema) => schema.notRequired()
      }),
      is_primary: yup.boolean(),
      type: yup.string().oneOf(['IMAGE', 'YOUTUBE_URL'], 'Invalid type')
    })
  ),
  facilities: yup.array(),
  lat: yup
    .number('${label} harus berupa angka')
    .typeError('Harus berupa angka')
    .label('Latitude')
    .lessThan(91, 'Format tidak sesuai')
    .moreThan(-91, 'Format tidak sesuai')
    .required('${label} wajib diisi'),
  lng: yup
    .number('${label} harus berupa angka')
    .typeError('Harus berupa angka')
    .label('Longitude')
    .lessThan(181, 'Format tidak sesuai')
    .moreThan(-181, 'Format tidak sesuai')
    .required('${label} wajib diisi'),
  site_address: yup.string().label('Alamat Site').required('${label} wajib diisi'),
  province: yup
    .object()
    .label('Provinsi')
    .shape({
      label: yup.string().required(),
      value: yup.string().required()
    })
    .required('${label} wajib dipilih'),
  city: yup
    .object()
    .label('Kota')
    .shape({
      label: yup.string().required(),
      value: yup.string().required()
    })
    .required('${label} wajib dipilih'),
  daily_quota: yup.number().label('Kuota Harian').required('${label} wajib diisi'),
  operational_email: yup
    .string()
    .label('Email Operasional')
    .email('Format alamat email salah')
    .required('${label} wajib diisi')
});

const TenantProfile = () => {
  const [isEditable, setIsEditable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initError, setInitError] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState('information');
  const [savedData, setSavedData] = useState(null);

  const form = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const getFacilities = async () => {
    const query = new URLSearchParams({
      page: 1,
      limit: 100,
      sort_field: 'name',
      sort_value: 'asc'
    }).toString();
    return await searchFacilities(query);
  };

  const getInitialData = async () => {
    try {
      setLoading(true);
      setInitError(false);
      const response = await getTenantDetail();
      const findFacilities = await getFacilities();
      const facilities = findFacilities.data.data.data;
      const responseData = response.data.data;
      const tenantFacilities = responseData.tenant_facilities;
      const facilityIdSet = new Set(tenantFacilities.map((item) => item.facility_id));
      const facilityValue = facilities.map((item) => ({
        ...item,
        is_checked: facilityIdSet.has(item.id)
      }));
      const mediaValue = responseData.tenant_media.map((item) => {
        return {
          url: item.url,
          is_primary: item.is_primary,
          type: item.type
        };
      });
      if (mediaValue.length < 5) {
        const restItems = 5 - mediaValue.length;
        for (let index = 0; index < restItems; index++) {
          mediaValue.push({
            url: '',
            is_primary: false,
            type: 'IMAGE'
          });
        }
      }

      const provinceValue = responseData.province
        ? { value: responseData.province.id, label: responseData.province.name }
        : null;

      const cityValue = responseData.city
        ? { value: responseData.city.id, label: responseData.city.name }
        : null;

      form.setValue('site_name', responseData.site_name);
      form.setValue('overview_en', responseData.overview_en ?? '');
      form.setValue('overview_id', responseData.overview_id ?? '');
      form.setValue('site_address', responseData.site_address ?? '');
      form.setValue('lat', responseData.lat ?? '');
      form.setValue('lng', responseData.lng ?? '');
      form.setValue('facilities', facilityValue);
      form.setValue('media', mediaValue);
      form.setValue('province', provinceValue);
      form.setValue('city', cityValue);
      form.setValue('daily_quota', responseData.daily_quota);
      form.setValue('operational_email', responseData.operational_email);
      setSavedData({
        site_name: responseData.site_name,
        overview_en: responseData.overview_en ?? '',
        overview_id: responseData.overview_id ?? '',
        site_address: responseData.site_address ?? '',
        lat: responseData.lat ?? '',
        lng: responseData.lng ?? '',
        facilities: facilityValue,
        media: mediaValue,
        province: provinceValue,
        city: cityValue,
        daily_quota: responseData.daily_quota ?? 0,
        operational_email: responseData.operational_email ?? ''
      });
      setLoading(false);
    } catch (error) {
      setInitError(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    getInitialData();
  }, []);

  const save = async (val) => {
    try {
      setSubmitLoading(true);
      const selectedFacilities = val.facilities.filter((item) => item.is_checked);
      const selectedMedia = val.media.filter((item) => item.url);
      const payload = {
        site_name: val.site_name,
        site_address: val.site_address,
        overview_en: val.overview_en,
        overview_id: val.overview_id,
        lat: `${val.lat}`,
        lng: `${val.lng}`,
        province_id: val.province ? val.province.value : null,
        city_id: val.city ? val.city.value : null,
        facilities: selectedFacilities.map((item) => item.id),
        media: selectedMedia,
        daily_quota: val.daily_quota ? val.daily_quota : 0,
        operational_email: val.operational_email ? val.operational_email : ''
      };
      await updateTenant(payload);
      await getInitialData();
      setSubmitLoading(false);
      setIsEditable(false);
      Swal.fire({
        title: 'Berhasil!',
        text: `Berhasil memperbarui profil tenant`,
        icon: 'success',
        confirmButtonText: 'Tutup'
      });
    } catch (error) {
      setSubmitLoading(false);
      Swal.fire({
        title: 'Gagal!',
        text: `Periksa koneksi internet anda dan coba kembali`,
        icon: 'error',
        confirmButtonText: 'Tutup'
      });
    }
  };

  const onSubmit = async (val) => {
    const totalMedia = val.media.filter((item) => item.url);
    if (totalMedia.length === 5) {
      await save(val);
    } else {
      Swal.fire({
        title: 'Apakah anda yakin?',
        text: 'Kami merekomendasikan untuk memiliki 4 media tambahan. Apakah anda tetap ingin melanjutkan?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Tetap lanjutkan!',
        cancelButtonText: 'Batalkan'
      }).then(async (result) => {
        if (result.isConfirmed) {
          await save(val);
        }
      });
    }
  };

  const handleCancel = () => {
    setIsEditable(false);
    form.reset(savedData);
  };

  return (
    <div className="row justify-between items-end">
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <h1 className="text-30 lh-14 fw-600">Profil</h1>

            {isEditable ? (
              <div>
                {!submitLoading && (
                  <button
                    type="button"
                    className="btn bg-white fw-500 text-16 text-dark btn-lg mr-20"
                    onClick={handleCancel}>
                    <i className="fa fa-close mr-10"></i>Batalkan
                  </button>
                )}
                <button
                  type="submit"
                  disabled={submitLoading}
                  className="btn btn-primary fw-500 text-16 text-white btn-lg">
                  {!submitLoading && <i className="fa fa-save mr-10"></i>}
                  {!submitLoading ? 'Simpan Perubahan' : 'Menyimpan...'}
                </button>
              </div>
            ) : (
              <div>
                {!loading && (
                  <button
                    type="button"
                    className="btn btn-primary fw-500 text-16 text-white btn-lg"
                    onClick={() => setIsEditable(true)}>
                    <i className="fa fa-pencil mr-10"></i>Edit Informasi Tenant
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="py-30 px-30 rounded-4 bg-white shadow-3 mt-20" style={{ minHeight: 420 }}>
            <div className="tabs -underline-2 js-tabs">
              <div className="tabs__controls row x-gap-40 y-gap-10 lg:x-gap-20 js-tabs-controls">
                <div className="col-auto">
                  <button
                    onClick={() => setSelectedMenu('information')}
                    type="button"
                    className={`tabs__button text-16 lg:text-16 text-light-1 fw-500 pb-5 lg:pb-0 js-tabs-button ${
                      selectedMenu === 'information' && 'is-tab-el-active'
                    }`}>
                    Informasi
                    {(form.formState.errors.site_name ||
                      form.formState.errors.overview_en ||
                      form.formState.errors.overview_id) && (
                      <span className="ml-10">
                        <i className="fa fa-exclamation-circle text-danger" />
                      </span>
                    )}
                  </button>
                </div>
                <div className="col-auto">
                  <button
                    onClick={() => setSelectedMenu('media')}
                    type="button"
                    className={`tabs__button text-16 lg:text-16 text-light-1 fw-500 pb-5 lg:pb-0 js-tabs-button ${
                      selectedMenu === 'media' && 'is-tab-el-active'
                    }`}>
                    Galeri
                    {form.formState.errors.media && (
                      <span className="ml-10">
                        <i className="fa fa-exclamation-circle text-danger" />
                      </span>
                    )}
                  </button>
                </div>
                <div className="col-auto">
                  <button
                    onClick={() => setSelectedMenu('facilities')}
                    type="button"
                    className={`tabs__button text-16 lg:text-16 text-light-1 fw-500 pb-5 lg:pb-0 js-tabs-button ${
                      selectedMenu === 'facilities' && 'is-tab-el-active'
                    }`}>
                    Fasilitas
                  </button>
                </div>
                <div className="col-auto">
                  <button
                    onClick={() => setSelectedMenu('location')}
                    type="button"
                    className={`tabs__button text-16 lg:text-16 text-light-1 fw-500 pb-5 lg:pb-0 js-tabs-button ${
                      selectedMenu === 'location' && 'is-tab-el-active'
                    }`}>
                    Lokasi
                    {(form.formState.errors.province ||
                      form.formState.errors.city ||
                      form.formState.errors.site_address ||
                      form.formState.errors.lat ||
                      form.formState.errors.lng) && (
                      <span className="ml-10">
                        <i className="fa fa-exclamation-circle text-danger" />
                      </span>
                    )}
                  </button>
                </div>
                <div className="col-auto">
                  <button
                    onClick={() => setSelectedMenu('configuration')}
                    type="button"
                    className={`tabs__button text-16 lg:text-16 text-light-1 fw-500 pb-5 lg:pb-0 js-tabs-button ${
                      selectedMenu === 'configuration' && 'is-tab-el-active'
                    }`}>
                    Konfigurasi
                    {form.formState.errors.daily_quota && (
                      <span className="ml-10">
                        <i className="fa fa-exclamation-circle text-danger" />
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
            {!loading && initError && (
              <div
                className="d-flex justify-content-center align-items-center fw-500"
                style={{ height: 420 }}>
                Gagal, periksa koneksi internet anda dan{' '}
                <span className="text-danger fw-600 cursor-pointer ml-10" onClick={getInitialData}>
                  Coba kembali
                </span>
              </div>
            )}
            {loading && !initError && (
              <div
                className="d-flex justify-content-center align-items-center fw-500"
                style={{ height: 420 }}>
                Memuat...
              </div>
            )}
            {!initError && !loading && selectedMenu === 'information' && (
              <Information isEditable={isEditable} form={form} loading={loading || submitLoading} />
            )}
            {!initError && !loading && selectedMenu === 'media' && (
              <Media isEditable={isEditable} form={form} loading={loading || submitLoading} />
            )}
            {!initError && !loading && selectedMenu === 'facilities' && (
              <Facilities isEditable={isEditable} form={form} loading={loading || submitLoading} />
            )}
            {!initError && !loading && selectedMenu === 'location' && (
              <Location isEditable={isEditable} form={form} loading={loading || submitLoading} />
            )}
            {!initError && !loading && selectedMenu === 'configuration' && (
              <Configuration
                isEditable={isEditable}
                form={form}
                loading={loading || submitLoading}
              />
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default TenantProfile;
